/**
 * "Hộ" của một hồ sơ: chính hồ sơ đó cộng mọi người thân đã liên kết (`patient_relations`,
 * chiều hồ sơ → người thân).
 *
 * Salepro không có bảng bệnh nhân: một tài khoản theo SĐT, tên người đi khám ghi trên từng
 * phiếu, và tab "Lịch sử khám" của tài khoản liệt kê mọi phiếu kèm tên đó. HIS giữ bệnh án theo
 * NGƯỜI, nên dữ liệu chuyển sang tách mỗi tên thành một hồ sơ và nối về tài khoản. Tài khoản
 * `Vũ Thoa` (dev, 03/11/2026) vì thế có 0 lịch hẹn của chính mình và 40 lịch của hai người con —
 * trang "Hồ sơ bệnh án" của chị trống trong khi salepro hiện đủ 40. Màn hình này gộp lại ở tầng
 * đọc: hỏi từng hồ sơ trong hộ rồi trộn theo ngày, mỗi dòng mang tên người khám.
 *
 * Hàm thuần, tách ra để test không phải dựng trang.
 */
import type { Relative } from "../../shared/api/patientRelations";

export interface Household {
  /** Hồ sơ đang xem đứng đầu, rồi tới người thân theo thứ tự API trả, không trùng. */
  ids: string[];
  /** Tên theo id, để hiện "Người khám" kể cả khi một dòng không mang tên. */
  nameOf: Record<string, string>;
  hasRelatives: boolean;
}

export function household(
  patientId: string,
  selfName: string | null | undefined,
  relatives: Relative[] | undefined,
): Household {
  const ids = [patientId];
  const nameOf: Record<string, string> = {};
  if (selfName) nameOf[patientId] = selfName;
  for (const r of relatives ?? []) {
    if (!r.relativePatientId || r.relativePatientId === patientId || ids.includes(r.relativePatientId)) {
      continue;
    }
    ids.push(r.relativePatientId);
    nameOf[r.relativePatientId] = r.fullName;
  }
  return { ids, nameOf, hasRelatives: ids.length > 1 };
}

/**
 * Trộn các trang (một trang mỗi hồ sơ) thành một danh sách, mới nhất trước. Trang `undefined`
 * (đang tải / lỗi) bị bỏ qua chứ không làm rỗng cả danh sách. Cùng ngày thì giữ thứ tự ổn định
 * theo id để hai lần vẽ không đổi chỗ nhau.
 */
export function mergeByDate<T extends { id: string }>(
  pages: ReadonlyArray<ReadonlyArray<T> | undefined>,
  dateOf: (row: T) => string | null | undefined,
): T[] {
  const out: T[] = [];
  const seen = new Set<string>();
  for (const page of pages) {
    for (const row of page ?? []) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      out.push(row);
    }
  }
  return out.sort((a, b) => {
    const da = dateOf(a) ?? "";
    const db = dateOf(b) ?? "";
    if (da !== db) return da < db ? 1 : -1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}
